// DependencyPanel — the asset's reference graph (roadmap D-4):
//   - "Depends on": resolved import edges, each flagged resolved / broken
//     (missing in depot) / external (engine plugin not hosted here).
//   - "Used by": the inverse — which other assets import this one.
// Data comes from /api/dam/deps, populated as a side-effect of bundling,
// so an asset that's never been previewed shows an empty-state hint.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';

type DepStatus = 'resolved' | 'missing' | 'external' | 'unknown';

interface DependencyRow {
  depUePath: string;
  depPath: string | null;
  status: DepStatus;
}
interface DependentRow {
  fromPath: string;
  fromRev: number | null;
}
interface DepsResponse {
  dependsOn: DependencyRow[];
  usedBy: DependentRow[];
}

type HrefFor = (depotPath: string) => { pathname: string; search: string };

export function DependencyPanel({
  depotPath,
  rev,
  hrefFor,
}: {
  depotPath: string;
  rev: number;
  hrefFor: HrefFor;
}) {
  const q = useQuery({
    queryKey: ['dam.deps', depotPath, rev],
    queryFn: () => fetchDeps(depotPath, rev),
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Loading references…</span>
      </div>
    );
  }
  if (q.error) {
    return (
      <Alert tone="destructive">
        <AlertDescription>{(q.error as Error).message}</AlertDescription>
      </Alert>
    );
  }

  const dependsOn = q.data?.dependsOn ?? [];
  const usedBy = q.data?.usedBy ?? [];
  const brokenCount = dependsOn.filter((d) => d.status === 'missing').length;

  if (dependsOn.length === 0 && usedBy.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No references recorded yet. The dependency graph is built when an asset
        is previewed — open this asset's preview (or one that references it) to
        populate it.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {brokenCount > 0 && (
        <Alert tone="destructive">
          <AlertDescription>
            {brokenCount} broken reference{brokenCount === 1 ? '' : 's'} — the
            imported asset{brokenCount === 1 ? ' is' : 's are'} not present in
            the depot.
          </AlertDescription>
        </Alert>
      )}

      <section className="flex flex-col gap-1.5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Depends on{dependsOn.length > 0 && ` (${dependsOn.length})`}
        </h3>
        {dependsOn.length === 0 ? (
          <p className="text-sm text-muted-foreground">No outgoing references.</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {dependsOn.map((d) => (
              <DependsOnRow key={d.depUePath} row={d} hrefFor={hrefFor} />
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-1.5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Used by{usedBy.length > 0 && ` (${usedBy.length})`}
        </h3>
        {usedBy.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing recorded references this asset yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {usedBy.map((u) => (
              <li key={u.fromPath} className="font-mono text-xs">
                <Link to={hrefFor(u.fromPath)} className="text-theia-400 hover:underline">
                  {assetLabel(u.fromPath)}
                </Link>
                <span className="ml-1.5 text-muted-foreground">{dirOf(u.fromPath)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function DependsOnRow({ row, hrefFor }: { row: DependencyRow; hrefFor: HrefFor }) {
  const label = row.depPath ? assetLabel(row.depPath) : ueLabel(row.depUePath);
  const sub = row.depPath ? dirOf(row.depPath) : row.depUePath;
  return (
    <li className="flex flex-wrap items-center gap-1.5 font-mono text-xs">
      {row.status === 'resolved' && row.depPath ? (
        <Link to={hrefFor(row.depPath)} className="text-theia-400 hover:underline">
          {label}
        </Link>
      ) : (
        <span className={row.status === 'missing' ? 'text-red-400' : 'text-foreground'}>
          {label}
        </span>
      )}
      <StatusBadge status={row.status} />
      <span className="text-muted-foreground">{sub}</span>
    </li>
  );
}

function StatusBadge({ status }: { status: DepStatus }) {
  if (status === 'resolved') return null; // the default; no badge keeps the list quiet
  if (status === 'missing') return <Badge tone="red">broken</Badge>;
  if (status === 'external') return <Badge tone="neutral">external</Badge>;
  return <Badge tone="yellow">unknown</Badge>;
}

async function fetchDeps(depotPath: string, rev: number): Promise<DepsResponse> {
  const res = await fetch(
    `/api/dam/deps?path=${encodeURIComponent(depotPath)}&rev=${rev}`,
    { credentials: 'include' },
  );
  if (!res.ok) throw new Error(`deps fetch failed: HTTP ${res.status}`);
  return (await res.json()) as DepsResponse;
}

// `//depot/.../Foo.uasset` -> `Foo`
function assetLabel(depotPath: string): string {
  const base = depotPath.slice(depotPath.lastIndexOf('/') + 1);
  return base.replace(/\.uasset$/i, '');
}
// `//depot/a/b/Foo.uasset` -> `a/b` (trimmed for context without the noise)
function dirOf(depotPath: string): string {
  const lastSlash = depotPath.lastIndexOf('/');
  const dir = lastSlash > 0 ? depotPath.slice(0, lastSlash) : '';
  return dir.replace(/^\/\/[^/]+\/[^/]+\//, ''); // drop `//depot/stream/`
}
// `/Engine/Foo/Bar` -> `Bar`
function ueLabel(uePath: string): string {
  return uePath.slice(uePath.lastIndexOf('/') + 1);
}
